import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tenantPaymentService } from '../../services/api';

interface ReceiptInfo {
    id: string;
    hash: string;
    issuedAt: string;
    downloadUrl: string | null;
}

interface ScheduleRow {
    periodYM: string;
    dueDate: string;
    amount: number;
    paidAmount: number;
    status: string;
    daysOverdue: number | null;
    receipt: ReceiptInfo | null;
}

interface Balance {
    totalExpected: number;
    totalPaid: number;
    arrears: number;
    missedMonths: number;
    lateMonths: number;
}

interface PortalData {
    tenantName: string;
    tenantEmail: string;
    propertyAddress: string;
    paymentRef: string;
    rentAmount: number;
    balance: Balance;
    status: string;
    nextDueDate: string | null;
    nextDueAmount: number | null;
    schedule: ScheduleRow[];
}

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
    ON_TIME:   { label: 'Paid on Time', color: 'text-emerald-700', bg: 'bg-emerald-50' },
    PAID_LATE: { label: 'Paid Late',    color: 'text-amber-700',  bg: 'bg-amber-50' },
    LATE:      { label: 'Late',         color: 'text-amber-700',  bg: 'bg-amber-50' },
    UPCOMING:  { label: 'Due Soon',     color: 'text-blue-700',   bg: 'bg-blue-50' },
    SCHEDULED: { label: 'Scheduled',    color: 'text-slate-600',  bg: 'bg-slate-50' },
    OVERDUE:   { label: 'Overdue',      color: 'text-orange-700', bg: 'bg-orange-50' },
    CRITICAL:  { label: 'Critical',     color: 'text-rose-700',   bg: 'bg-rose-50' },
    UNKNOWN:   { label: 'Unknown',      color: 'text-gray-600',   bg: 'bg-gray-50' },
};

const formatYM = (ym: string) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function TenantPaymentPortal() {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<PortalData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetch = async () => {
            if (!token) {
                setError('Invalid link.');
                setLoading(false);
                return;
            }
            try {
                const res = await tenantPaymentService.getPortal(token);
                if (res.data.success) {
                    setData(res.data.data);
                } else {
                    setError('Payment information not available.');
                }
            } catch (err: any) {
                const status = err.response?.status;
                if (status === 410) {
                    setError('This link has expired. Please ask your landlord for a new one.');
                } else if (status === 404) {
                    setError('Invalid or expired link.');
                } else {
                    setError(err.response?.data?.message || 'Failed to load payment information.');
                }
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500">Loading your payment information…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <div className="text-gray-400 text-5xl mb-4">&#9888;</div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Unavailable</h1>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const st = STATUS_STYLE[data.status] || STATUS_STYLE.UNKNOWN;

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8 font-sans">
            <div className="max-w-2xl mx-auto space-y-6">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="bg-emerald-700 px-6 py-5">
                        <h1 className="text-xl font-bold text-white">Rent Payment Portal</h1>
                        <p className="text-emerald-100 text-sm mt-1">{data.tenantName}</p>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Property</p>
                                <p className="text-sm font-medium text-gray-900">{data.propertyAddress || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Payment Reference</p>
                                <p className="text-sm font-medium text-gray-900">{data.paymentRef || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Monthly Rent</p>
                                <p className="text-sm font-medium text-gray-900">&pound;{(data.rentAmount || 0).toFixed(2)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Status</p>
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${st.bg} ${st.color}`}>{st.label}</span>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <p className="text-2xl font-black text-gray-900">&pound;{data.balance.arrears.toFixed(2)}</p>
                                    <p className="text-[10px] text-gray-500 uppercase font-semibold">Outstanding</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-black text-gray-900">&pound;{data.balance.totalPaid.toFixed(2)}</p>
                                    <p className="text-[10px] text-gray-500 uppercase font-semibold">Total Paid</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-black text-gray-900">{data.balance.totalExpected > 0
                                        ? Math.round((data.balance.totalPaid / data.balance.totalExpected) * 100)
                                        : 100}%</p>
                                    <p className="text-[10px] text-gray-500 uppercase font-semibold">Collection Rate</p>
                                </div>
                            </div>
                        </div>

                        {data.nextDueDate && (
                            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-6 rounded-r-lg">
                                <p className="text-xs text-blue-800">
                                    <span className="font-bold">Next payment due:</span> {formatDate(data.nextDueDate)}
                                    {data.nextDueAmount ? <span> &mdash; &pound;{data.nextDueAmount.toFixed(2)}</span> : ''}
                                </p>
                            </div>
                        )}

                        <h2 className="text-sm font-black text-gray-900 uppercase tracking-tight mb-3">Payment History</h2>

                        <div className="space-y-2">
                            {data.schedule.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">No payment history available.</p>
                            )}
                            {data.schedule.slice().reverse().map((row) => {
                                const s = STATUS_STYLE[row.status] || STATUS_STYLE.UNKNOWN;
                                return (
                                    <div key={row.periodYM} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <div className="px-4 py-3 flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg ${s.bg} ${s.color} flex items-center justify-center text-xs font-bold`}>
                                                {row.status === 'ON_TIME' || row.status === 'PAID_LATE' ? '$' : '!'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold text-gray-900">{formatYM(row.periodYM)}</div>
                                                <div className="text-[10px] text-gray-500">
                                                    Due: {formatDate(row.dueDate)}
                                                    {row.daysOverdue !== null && row.daysOverdue > 0 && (
                                                        <> &middot; <span className="text-rose-600 font-bold">{row.daysOverdue} day(s) overdue</span></>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-bold text-gray-900">
                                                    &pound;{(row.paidAmount || 0).toFixed(2)} <span className="text-gray-400 text-xs">/ &pound;{row.amount.toFixed(2)}</span>
                                                </div>
                                                <div className={`text-[9px] font-bold uppercase ${s.color}`}>{s.label}</div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {row.receipt && row.receipt.downloadUrl && (
                                                    <a
                                                        href={row.receipt.downloadUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg text-[10px] font-bold uppercase tracking-tighter hover:bg-emerald-700"
                                                    >
                                                        Receipt
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="text-center text-gray-400 text-xs">
                    &copy; 2026 Landlord Pro Compliance Systems &middot; Secured payment records
                </div>
            </div>
        </div>
    );
}
